import { useClipboard } from '@orn/shared-web/hooks/useClipboard';
import { CopyIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';

import IconButton from '../IconButton';
import { useToast } from '../Toast';

export interface ICopyButtonProps {
  /** The full text to put on the clipboard — never the clipped text a cell shows. */
  value: string;
  /** Accessible label and hover tooltip, e.g. "Copy id". */
  label: string;
  /** Toast shown once the copy lands, e.g. "Id copied." */
  successMessage: string;
  className?: string;
}

/**
 * Icon-only copy-to-clipboard control for dense rows. Confirms with a toast,
 * and on a refused clipboard write tells the operator to copy by hand rather
 * than failing silently.
 */
const CopyButton = ({ value, label, successMessage, className }: ICopyButtonProps) => {
  const { showToast } = useToast();
  const { copy } = useClipboard();
  const handleCopy = async () => {
    const { success, error } = await copy(value);
    if (success) {
      showToast({ type: 'success', message: successMessage });
      return;
    }
    logger.error('Clipboard write failed', error);
    showToast({ type: 'error', message: 'Could not copy — select the text and copy it by hand.' });
  };
  return (
    <IconButton label={label} className={className} onClick={() => void handleCopy()}>
      <CopyIcon className="size-3.5" />
    </IconButton>
  );
};

export default CopyButton;
